import React, { Children } from 'react';
import { motion } from 'framer-motion';
interface MockDashboardProps {
  variant?: 'main' | 'finance' | 'hr' | 'brand' | 'pricing' | 'legal' | 'docs';
  className?: string;
}
export function MockDashboard({
  variant = 'main',
  className = ''
}: MockDashboardProps) {
  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -10
    },
    visible: {
      opacity: 1,
      x: 0
    }
  };
  // Render different content based on variant
  const renderContent = () => {
    switch (variant) {
      case 'finance':
        return <FinanceContent itemVariants={itemVariants} data-id="element-142" />;
      case 'hr':
        return <HRContent itemVariants={itemVariants} data-id="element-143" />;
      case 'brand':
        return <BrandContent itemVariants={itemVariants} data-id="element-144" />;
      case 'pricing':
        return <PricingContent itemVariants={itemVariants} data-id="element-145" />;
      case 'legal':
        return <LegalContent itemVariants={itemVariants} data-id="element-146" />;
      case 'docs':
        return <DocsContent itemVariants={itemVariants} data-id="element-147" />;
      default:
        return <MainContent itemVariants={itemVariants} data-id="element-148" />;
    }
  };
  return <motion.div className={`relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden ${className}`} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={containerVariants} data-id="element-149">
      {/* Top Bar */}
      <div className="h-12 border-b border-slate-100 flex items-center px-4 space-x-4 bg-slate-50/50" data-id="element-150">
        <div className="flex space-x-2" data-id="element-151">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-152"></div>
          <div className="w-3 h-3 rounded-full bg-amber-400" data-id="element-153"></div>
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-154"></div>
        </div>
        <div className="flex-1 bg-white h-8 rounded-lg border border-slate-200 max-w-md mx-auto" data-id="element-155"></div>
        <div className="w-8 h-8 rounded-full bg-indigo-100 border border-indigo-200" data-id="element-156"></div>
      </div>

      <div className="flex h-[400px] md:h-[500px]" data-id="element-157">
        {/* Sidebar */}
        <div className="w-16 md:w-48 border-r border-slate-100 bg-slate-50/30 flex flex-col py-4 px-2 md:px-4 space-y-4" data-id="element-158">
          <div className="space-y-2" data-id="element-159">
            {[1, 2, 3, 4, 5].map(i => <motion.div key={i} variants={itemVariants} className={`h-8 rounded-lg w-full ${i === 1 ? 'bg-indigo-100' : 'hover:bg-slate-100'}`} data-id="element-160"></motion.div>)}
          </div>
          <div className="mt-auto space-y-2" data-id="element-161">
            <motion.div variants={itemVariants} className="h-8 rounded-lg w-full bg-slate-100" data-id="element-162"></motion.div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 bg-white p-4 md:p-6 overflow-hidden relative" data-id="element-163">
          {renderContent()}
        </div>
      </div>
    </motion.div>;
}
// Sub-components for different dashboard variants
function MainContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-164">
      <div className="flex justify-between items-center mb-6" data-id="element-165">
        <motion.div variants={itemVariants} className="h-8 w-48 bg-slate-100 rounded-lg" data-id="element-166"></motion.div>
        <motion.div variants={itemVariants} className="h-8 w-24 bg-indigo-600 rounded-lg" data-id="element-167"></motion.div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-3 gap-4" data-id="element-168">
        {[1, 2, 3].map(i => <motion.div key={i} variants={itemVariants} className="h-24 rounded-xl border border-slate-100 p-4 shadow-sm bg-white" data-id="element-169">
            <div className="h-4 w-16 bg-slate-100 rounded mb-3" data-id="element-170"></div>
            <div className="h-8 w-24 bg-slate-800 rounded" data-id="element-171"></div>
          </motion.div>)}
      </div>

      {/* Chart Area */}
      <div className="grid grid-cols-3 gap-4 h-48" data-id="element-172">
        <motion.div variants={itemVariants} className="col-span-2 rounded-xl border border-slate-100 p-4 shadow-sm bg-white relative overflow-hidden" data-id="element-173">
          <div className="h-4 w-32 bg-slate-100 rounded mb-4" data-id="element-174"></div>
          <div className="flex items-end space-x-2 h-32 w-full mt-4" data-id="element-175">
            {[40, 70, 45, 90, 65, 85, 55, 80, 60, 75].map((h, i) => <div key={i} className="flex-1 bg-indigo-500 rounded-t-sm opacity-80" style={{
            height: `${h}%`
          }} data-id="element-176"></div>)}
          </div>
        </motion.div>
        <motion.div variants={itemVariants} className="col-span-1 rounded-xl border border-slate-100 p-4 shadow-sm bg-white" data-id="element-177">
          <div className="h-4 w-20 bg-slate-100 rounded mb-4" data-id="element-178"></div>
          <div className="w-24 h-24 rounded-full border-8 border-teal-400 border-t-indigo-500 mx-auto mt-4" data-id="element-179"></div>
        </motion.div>
      </div>

      {/* Table Rows */}
      <div className="space-y-2" data-id="element-180">
        {[1, 2, 3].map(i => <motion.div key={i} variants={itemVariants} className="h-10 w-full bg-slate-50 rounded-lg border border-slate-100" data-id="element-181"></motion.div>)}
      </div>
    </div>;
}
function FinanceContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-182">
      <motion.div variants={itemVariants} className="h-8 w-64 bg-slate-100 rounded-lg mb-6" data-id="element-183"></motion.div>

      <div className="grid grid-cols-2 gap-4" data-id="element-184">
        <motion.div variants={itemVariants} className="h-32 rounded-xl bg-indigo-50 border border-indigo-100 p-4" data-id="element-185">
          <div className="h-4 w-24 bg-indigo-200 rounded mb-2" data-id="element-186"></div>
          <div className="h-10 w-32 bg-indigo-600 rounded" data-id="element-187"></div>
        </motion.div>
        <motion.div variants={itemVariants} className="h-32 rounded-xl bg-white border border-slate-100 p-4" data-id="element-188">
          <div className="h-4 w-24 bg-slate-100 rounded mb-2" data-id="element-189"></div>
          <div className="h-10 w-32 bg-slate-800 rounded" data-id="element-190"></div>
        </motion.div>
      </div>

      <motion.div variants={itemVariants} className="h-48 rounded-xl border border-slate-100 p-4 bg-white" data-id="element-191">
        <div className="flex items-end h-full space-x-4" data-id="element-192">
          {[30, 50, 45, 60, 80, 75, 90].map((h, i) => <div key={i} className="flex-1 bg-indigo-500 rounded-t-md" style={{
          height: `${h}%`
        }} data-id="element-193"></div>)}
        </div>
      </motion.div>
    </div>;
}
function HRContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-194">
      <motion.div variants={itemVariants} className="flex justify-between mb-6" data-id="element-195">
        <div className="h-8 w-40 bg-slate-100 rounded-lg" data-id="element-196"></div>
        <div className="h-8 w-24 bg-teal-500 rounded-lg" data-id="element-197"></div>
      </motion.div>

      <div className="grid grid-cols-4 gap-3" data-id="element-198">
        {[1, 2, 3, 4].map(i => <motion.div key={i} variants={itemVariants} className="h-20 rounded-xl border border-slate-100 bg-white p-3 text-center flex flex-col items-center justify-center" data-id="element-199">
            <div className="h-6 w-6 rounded-full bg-teal-100 mb-2" data-id="element-200"></div>
            <div className="h-3 w-12 bg-slate-100 rounded" data-id="element-201"></div>
          </motion.div>)}
      </div>

      <motion.div variants={itemVariants} className="h-56 rounded-xl border border-slate-100 p-4 bg-white flex items-center justify-center" data-id="element-202">
        <div className="w-full space-y-3" data-id="element-203">
          {[1, 2, 3, 4].map(i => <div key={i} className="flex items-center space-x-3" data-id="element-204">
              <div className="w-8 h-8 rounded-full bg-slate-100" data-id="element-205"></div>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-206">
                <div className="h-full bg-teal-500" style={{
              width: `${Math.random() * 60 + 20}%`
            }} data-id="element-207"></div>
              </div>
            </div>)}
        </div>
      </motion.div>
    </div>;
}
function BrandContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-208">
      <motion.div variants={itemVariants} className="h-8 w-48 bg-slate-100 rounded-lg mb-6" data-id="element-209"></motion.div>

      <div className="grid grid-cols-2 gap-4" data-id="element-210">
        <motion.div variants={itemVariants} className="h-40 rounded-xl border border-slate-100 p-4 bg-white" data-id="element-211">
          <div className="h-4 w-24 bg-slate-100 rounded mb-4" data-id="element-212"></div>
          <div className="w-24 h-24 rounded-full border-8 border-amber-400 border-r-transparent mx-auto" data-id="element-213"></div>
        </motion.div>
        <motion.div variants={itemVariants} className="h-40 rounded-xl border border-slate-100 p-4 bg-white space-y-3" data-id="element-214">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center space-x-2" data-id="element-215">
              <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-216"></div>
              <div className="h-3 w-full bg-slate-50 rounded" data-id="element-217"></div>
            </div>)}
        </motion.div>
      </div>

      <div className="space-y-3" data-id="element-218">
        {[1, 2].map(i => <motion.div key={i} variants={itemVariants} className="h-16 rounded-xl border border-slate-100 p-3 bg-white flex items-center space-x-3" data-id="element-219">
            <div className="w-10 h-10 rounded-full bg-slate-100" data-id="element-220"></div>
            <div className="flex-1 space-y-2" data-id="element-221">
              <div className="h-3 w-3/4 bg-slate-100 rounded" data-id="element-222"></div>
              <div className="h-2 w-1/2 bg-slate-50 rounded" data-id="element-223"></div>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
function PricingContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-224">
      <motion.div variants={itemVariants} className="h-8 w-56 bg-slate-100 rounded-lg mb-6" data-id="element-225"></motion.div>

      <motion.div variants={itemVariants} className="h-64 rounded-xl border border-slate-100 p-4 bg-white relative" data-id="element-226">
        <div className="absolute inset-x-4 top-4 bottom-4 flex items-end justify-between space-x-2" data-id="element-227">
          {[30, 45, 40, 60, 55, 70, 65, 80, 75, 90, 85, 100].map((h, i) => <div key={i} className="w-full bg-emerald-500 rounded-t-sm opacity-80" style={{
          height: `${h}%`
        }} data-id="element-228"></div>)}
        </div>
        <div className="absolute top-4 right-4 bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-bold" data-id="element-229">
          +12% vs Competitors
        </div>
      </motion.div>

      <div className="grid grid-cols-3 gap-3" data-id="element-230">
        {[1, 2, 3].map(i => <motion.div key={i} variants={itemVariants} className="h-16 rounded-lg bg-slate-50 border border-slate-100" data-id="element-231"></motion.div>)}
      </div>
    </div>;
}
function LegalContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-232">
      <motion.div variants={itemVariants} className="h-8 w-48 bg-slate-100 rounded-lg mb-6" data-id="element-233"></motion.div>

      <div className="grid grid-cols-3 gap-4" data-id="element-234">
        {[1, 2, 3].map(i => <motion.div key={i} variants={itemVariants} className="h-24 rounded-xl border border-slate-100 bg-white p-3 flex flex-col justify-between" data-id="element-235">
            <div className="w-8 h-8 rounded bg-violet-100" data-id="element-236"></div>
            <div className="h-3 w-16 bg-slate-200 rounded" data-id="element-237"></div>
          </motion.div>)}
      </div>

      <div className="space-y-3" data-id="element-238">
        <motion.div variants={itemVariants} className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-239">
          Recent Contracts
        </motion.div>
        {[1, 2, 3].map(i => <motion.div key={i} variants={itemVariants} className="h-12 rounded-lg border border-slate-100 bg-white flex items-center px-3 justify-between" data-id="element-240">
            <div className="flex items-center space-x-3" data-id="element-241">
              <div className="w-6 h-8 bg-slate-100 rounded-sm" data-id="element-242"></div>
              <div className="h-3 w-32 bg-slate-100 rounded" data-id="element-243"></div>
            </div>
            <div className="h-6 w-16 bg-violet-100 rounded-full" data-id="element-244"></div>
          </motion.div>)}
      </div>
    </div>;
}
function DocsContent({
  itemVariants
}: {
  itemVariants: any;
}) {
  return <div className="space-y-6" data-id="element-245">
      <motion.div variants={itemVariants} className="h-8 w-48 bg-slate-100 rounded-lg mb-6" data-id="element-246"></motion.div>

      <div className="flex space-x-4 h-64" data-id="element-247">
        <motion.div variants={itemVariants} className="w-1/2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-248">
          <div className="w-12 h-12 rounded-full bg-rose-100 mb-3" data-id="element-249"></div>
          <div className="h-3 w-24 bg-slate-200 rounded" data-id="element-250"></div>
        </motion.div>

        <div className="w-1/2 space-y-3" data-id="element-251">
          {[1, 2, 3, 4].map(i => <motion.div key={i} variants={itemVariants} className="h-12 rounded-lg border border-slate-100 bg-white flex items-center px-3 space-x-3" data-id="element-252">
              <div className="w-8 h-8 bg-slate-100 rounded" data-id="element-253"></div>
              <div className="flex-1 space-y-1" data-id="element-254">
                <div className="h-2 w-full bg-slate-100 rounded" data-id="element-255"></div>
                <div className="h-2 w-2/3 bg-slate-50 rounded" data-id="element-256"></div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}